import cx from 'classnames';
import { Fragment, ReactNode, useCallback, useMemo } from 'react';

import { escapeRegExp } from '../../../../../utils/escapeRegExp';
import Button from '../../../button';
import PartNameWithStock from '../../../v2/categories/common/part-name-with-stock';
import useTranslation from 'next-translate/useTranslation';

type TFilteredList = {
  groups: TCategories;
  searchText: string;
  onChange?: (category: TPart) => void;
  current: { category: TCategory; part: TPart } | null;
  showStock?: boolean;
  minH?: boolean;
};

const FilteredList = ({
  groups,
  onChange,
  searchText,
  current,
  minH,
}: TFilteredList) => {
  const { t } = useTranslation();

  const regex = useMemo(() => {
    return new RegExp(
      escapeRegExp(
        searchText
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      ),
      'i'
    );
  }, [searchText]);

  const partName = useCallback(
    (name: string) => {
      const nameNormalized = name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

      const normalizedSearchText = searchText
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

      const matchIndex = nameNormalized.indexOf(normalizedSearchText);

      if (matchIndex === -1) return <span>{name}</span>;

      const start = name.slice(0, matchIndex);
      const match = name.slice(
        matchIndex,
        matchIndex + normalizedSearchText.length
      );
      const end = name.slice(matchIndex + normalizedSearchText.length);

      return (
        <Fragment>
          {start}
          <span className="font-bold text-brand-100">{match}</span>
          {end}
        </Fragment>
      );
    },
    [regex]
  );

  return (
    <div
      className={cx(
        'relative after:absolute',
        minH ? 'overflow-hidden' : 'overflow-auto'
      )}
    >
      <div
        className={cx(
          'relative',
          minH ? 'min-h-[300px] max-h-[460px] overflow-auto' : 'min-h-full'
        )}
      >
        <ul>
          {groups.length > 0 ? (
            groups.map((g) => {
              return Object.entries(g.parts).map(([_, pArray]) => {
                return [
                  pArray?.map((p) => (
                    <li key={p.id}>
                      <Button
                        disabled={p.total_products <= 0}
                        onClick={(e) => {
                          onChange && onChange(p);
                        }}
                        className={cx(
                          '!text-base leading-[48px] text-left !min-h-12 !py-2 flex flex-row justify-between items-center grow-0',
                          {
                            'bg-brand-10': current?.part?.slug === p.slug,
                          },
                          {
                            'cursor-default disabled:bg-white':
                              p.total_products <= 0,
                          }
                        )}
                      >
                        <PartNameWithStock
                          name={partName(p.name)}
                          slug={p.slug}
                          totalStock={p.total_products}
                          inverted
                          display="column"
                          isChecked={current?.part?.slug === p.slug}
                          stockWithBg={true}
                          displayCheck={false}
                          showStock={p.total_products > 0 ? true : false}
                        />
                      </Button>
                    </li>
                  )),
                ];
              });
            })
          ) : (
            <>
              <div className={cx('text-center', 'mt-12')}>
                <p className={cx('mb-6')}>{t('common:noresults')}</p>
              </div>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default FilteredList;
